import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PermMatrix from "@/components/PermMatrix";
import { Plus, X, Save, Truck, MoreVertical, Pencil, Trash2, Building, Eye, Mail, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { EmailBannerBlock } from "@/components/EmailBannerBlock";
import { EmailLogsTab } from "@/components/EmailLogsTab";

const fileToImage = (file, max = 480) => new Promise((resolve) => {
  const r = new FileReader();
  r.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > max) { height = height * max / width; width = max; }
      else if (height > max) { width = width * max / height; height = max; }
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL("image/png"));
    };
    img.src = ev.target.result;
  };
  r.readAsDataURL(file);
});
const IMG_FIELDS = [["logo_url", "Logo"], ["signature", "Signature"], ["cachet", "Cachet"]];

function ImgUpload({ label, value, onChange }) {
  const ref = useRef();
  const pick = async (e) => { const f = e.target.files?.[0]; if (!f) return; onChange(await fileToImage(f)); toast.success(`${label} ajouté`); };
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs font-medium mb-2">{label}</p>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-24 items-center justify-center rounded border bg-slate-50 overflow-hidden">
          {value ? <img src={value} alt={label} className="max-h-16 max-w-24 object-contain" /> : <span className="text-[10px] text-slate-300">Aucun</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <input type="file" accept="image/*" ref={ref} className="hidden" onChange={pick} data-testid={`upload-${label}`} />
          <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()}>Téléverser</Button>
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-rose-500 text-left">Retirer</button>}
        </div>
      </div>
    </div>
  );
}

const GROUPS = [
  { title: "Organisation", keys: [
    ["poles", "Pôles"], ["functions", "Fonctions"], ["contract_types", "Types de contrats de travail"],
    ["collaborator_statuses", "Statuts des collaborateurs"], ["matrimonial_statuses", "Situations matrimoniales"],
    ["id_types", "Types de pièce d'identité"],
  ]},
  { title: "Commercial", keys: [
    ["interest_levels", "Niveaux d'intérêt"], ["pipeline_stages", "Étapes des opportunités"],
    ["sources", "Sources des prospects"], ["prospect_types", "Types de prospects"],
    ["client_types", "Types de clients"], ["project_categories", "Catégories de projets"],
    ["project_types", "Types de projets"],
  ]},
  { title: "Stocks", keys: [
    ["article_categories", "Catégories d'articles"], ["article_subcategories", "Sous-catégories d'articles"],
    ["units", "Unités"], ["movement_types", "Types de mouvements"], ["adjustment_reasons", "Motifs d'ajustement"],
  ]},
  { title: "Finance", keys: [
    ["payment_methods", "Modes de paiement"], ["banks", "Banques"], ["telecom_operators", "Opérateurs télécom"],
    ["cash_types", "Types de caisses"], ["expense_types", "Types de dépenses"], ["recette_types", "Types de recettes"],
    ["charge_categories", "Catégories de charges"], ["tax_types", "Types d'impôts & taxes"],
    ["contribution_types", "Types de cotisations sociales"], ["other_thirdparties", "Autres tiers (OTR)"],
  ]},
  { title: "Courriers", keys: [
    ["mail_transmission_means", "Moyens de transmission"], ["mail_types", "Types de courriers"],
    ["mail_statuses", "Statuts des courriers"],
  ]},
  { title: "RH & divers", keys: [
    ["leave_types", "Types de congés"], ["countries", "Pays"], ["cities", "Villes"],
    ["expense_categories", "Catégories de dépenses (projets)"],
  ]},
];
const ALL_KEYS = GROUPS.flatMap((g) => g.keys.map((k) => k[0]));

const COMPANY_FIELDS = [
  { key: "name", label: "Nom de l'entreprise" }, { key: "raison_sociale", label: "Raison sociale" }, { key: "nif", label: "NIF" }, { key: "rccm", label: "RCCM" },
  { key: "phone", label: "Téléphone" }, { key: "email", label: "E-mail" }, { key: "website", label: "Site web" },
  { key: "signataire_nom", label: "Signataire habilité — Nom complet" },
  { key: "signataire_fonction", label: "Signataire habilité — Fonction / titre" },
  { key: "logo_url", label: "URL du logo", col: 2 }, { key: "address", label: "Adresse", type: "textarea", col: 2 },
  { key: "bank_details", label: "Coordonnées bancaires", type: "textarea", col: 2 },
  { key: "legal_mentions", label: "Mentions légales", type: "textarea", col: 2 },
  { key: "cgv", label: "Conditions générales de vente", type: "textarea", col: 2 },
  { key: "extra", label: "Informations complémentaires (pied de page)", type: "textarea", col: 2 },
  { key: "extra_header", label: "Autres informations de l'en-tête (courriers)", col: 2 },
  { key: "footer_text", label: "Texte personnalisé du pied de page (courriers)", type: "textarea", col: 2 },
  { key: "email_expediteur", label: "E-mail d'expédition des documents (devis, factures, contrats, BC)", col: 2 },
  { key: "email_expediteur_nom", label: "Nom d'expéditeur affiché" },
  { key: "email_reply_to", label: "E-mail de réponse (reply-to)" },
];
const VEHICLE_STATUSES = ["Actif", "En maintenance", "Hors service"];

const RELANCE_PALIERS = [
  { p: 1, label: "Rappel (J+3)", color: "#076633" },
  { p: 2, label: "Relance (J+7)", color: "#c2410c" },
  { p: 3, label: "Mise en demeure (J+15)", color: "#b91c1c" },
];
const RELANCE_TOKENS = ["{client}", "{montant_du}", "{nb_factures}", "{entreprise}", "{echeance_max}", "{jours_retard}"];

function ListBlock({ label, list, onAdd, onRemove }) {
  const [v, setV] = useState("");
  return (
    <div className="rounded-xl border bg-white p-4">
      <h3 className="font-head font-semibold mb-2 text-sm">{label}</h3>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(list || []).map((x, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-2.5 pr-1.5 py-0.5 text-xs">
            {x}<button onClick={() => onRemove(i)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {(!list || list.length === 0) && <span className="text-xs text-slate-400">Vide</span>}
      </div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), (v.trim() && (onAdd(v.trim()), setV(""))))}
          placeholder="Ajouter…" className="h-8" data-testid={`add-input-${label}`} />
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(""); } }}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function SubcatMapBlock({ s, setS }) {
  const cats = s.article_categories || [];
  const map = s.article_subcategory_map || {};
  const [cat, setCat] = useState(cats[0] || "");
  const [v, setV] = useState("");
  const subs = (cat && map[cat]) || [];
  const setMap = (next) => setS((p) => ({ ...p, article_subcategory_map: next }));
  const add = () => { if (!cat || !v.trim()) return; setMap({ ...map, [cat]: [...(map[cat] || []), v.trim()] }); setV(""); };
  const rm = (i) => setMap({ ...map, [cat]: (map[cat] || []).filter((_, j) => j !== i) });
  return (
    <div className="rounded-xl border bg-white p-4" data-testid="subcat-map-block">
      <h3 className="font-head font-semibold mb-1 text-sm">Sous-catégories par catégorie</h3>
      <p className="text-xs text-slate-500 mb-3">Associez des sous-catégories à chaque catégorie. À la création d'un article, la liste des sous-catégories est filtrée selon la catégorie choisie.</p>
      <div className="mb-3">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-8 w-full sm:w-64" data-testid="subcat-map-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {cat ? (<>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {subs.map((x, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-100 pl-2.5 pr-1.5 py-0.5 text-xs">
              {x}<button onClick={() => rm(i)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {subs.length === 0 && <span className="text-xs text-slate-400">Aucune — la liste globale sera utilisée.</span>}
        </div>
        <div className="flex gap-2">
          <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={`Ajouter à « ${cat} »…`} className="h-8" data-testid="subcat-map-input" />
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={add} data-testid="subcat-map-add"><Plus className="h-4 w-4" /></Button>
        </div>
      </>) : <p className="text-xs text-slate-400">Créez d'abord des catégories d'articles ci-dessus.</p>}
    </div>
  );
}

function ListsTab({ s, setS }) {
  const add = (key, val) => setS((p) => ({ ...p, [key]: [...(p[key] || []), val] }));
  const rm = (key, i) => setS((p) => ({ ...p, [key]: p[key].filter((_, j) => j !== i) }));
  return (
    <div className="space-y-6">
      {GROUPS.map((g) => (
        <div key={g.title}>
          <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 mb-2">{g.title}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {g.keys.map(([key, label]) => (
              <ListBlock key={key} label={label} list={s[key]} onAdd={(v) => add(key, v)} onRemove={(i) => rm(key, i)} />
            ))}
          </div>
        </div>
      ))}
      <div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 mb-2">Stocks — Sous-catégories liées</p>
        <SubcatMapBlock s={s} setS={setS} />
      </div>
    </div>
  );
}

function RelanceBlock({ c, set }) {
  const [preview, setPreview] = useState(null);
  const [loadingP, setLoadingP] = useState(0);
  const doPreview = async (p) => {
    setLoadingP(p);
    try {
      const r = await api.post("/receivables/relance-preview", {
        palier: p,
        intro: c[`relance_intro_${p}`] || "",
        signature: c.relance_signature || "",
        objet: c[`relance_objet_${p}`] || "",
        sms: c[`relance_sms_${p}`] || "",
      });
      setPreview(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoadingP(0); }
  };
  const insertToken = (key, tok) => set(key, `${c[key] || ""}${tok}`);
  return (
    <div className="rounded-xl border bg-white p-6" data-testid="relance-settings">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4 text-brand-600" />
        <h3 className="font-head font-semibold text-sm">E-mails de relance de créances</h3>
      </div>
      <p className="text-sm text-slate-500 mb-3">Personnalisez l'objet et le texte d'introduction de chaque palier, ainsi que la signature. Laissez vide pour utiliser les textes par défaut.</p>
      <div className="rounded-lg bg-slate-50 border p-3 mb-5">
        <p className="text-xs font-medium text-slate-600 mb-1.5">Variables disponibles (cliquez pour insérer dans le dernier champ actif — ou tapez-les) :</p>
        <div className="flex flex-wrap gap-1.5">
          {RELANCE_TOKENS.map((t) => (
            <span key={t} className="inline-block rounded-md bg-white border px-2 py-0.5 text-xs font-mono text-brand-700" data-testid={`relance-token-${t}`}>{t}</span>
          ))}
        </div>
      </div>
      <div className="space-y-5">
        {RELANCE_PALIERS.map(({ p, label, color }) => (
          <div key={p} className="rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{label}
              </span>
              <Button variant="outline" size="sm" onClick={() => doPreview(p)} disabled={loadingP === p} data-testid={`relance-preview-btn-${p}`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" />{loadingP === p ? "…" : "Aperçu"}
              </Button>
            </div>
            <div className="grid gap-3">
              <div>
                <Label className="text-xs">Objet de l'e-mail</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={c[`relance_objet_${p}`] || ""} onChange={(e) => set(`relance_objet_${p}`, e.target.value)}
                    placeholder="Objet par défaut" data-testid={`company-relance_objet_${p}`} />
                  {RELANCE_TOKENS.slice(0, 2).map((t) => (
                    <Button key={t} type="button" variant="ghost" size="sm" className="text-xs font-mono px-1.5" onClick={() => insertToken(`relance_objet_${p}`, t)}>{t}</Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Texte d'introduction (e-mail)</Label>
                <Textarea value={c[`relance_intro_${p}`] || ""} onChange={(e) => set(`relance_intro_${p}`, e.target.value)}
                  rows={3} placeholder="Texte par défaut" data-testid={`company-relance_intro_${p}`} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Message SMS (court, ~160 caractères)</Label>
                <Textarea value={c[`relance_sms_${p}`] || ""} onChange={(e) => set(`relance_sms_${p}`, e.target.value)}
                  rows={2} placeholder="SMS par défaut" data-testid={`company-relance_sms_${p}`} className="mt-1" />
                <p className="text-[11px] text-slate-400 mt-1">{(c[`relance_sms_${p}`] || "").length} caractères · envoyé au client si un numéro Twilio valide est configuré.</p>
              </div>
            </div>
          </div>
        ))}
        <div>
          <Label className="text-xs">Signature / pied de page (commun aux paliers)</Label>
          <Textarea value={c.relance_signature || ""} onChange={(e) => set("relance_signature", e.target.value)}
            rows={3} placeholder="Ex : Le service comptabilité — {entreprise}" data-testid="company-relance_signature" className="mt-1" />
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="relance-preview-dialog">
          <DialogHeader><DialogTitle>Aperçu — {preview?.titre}</DialogTitle></DialogHeader>
          {preview && (
            <div>
              <p className="text-xs text-slate-500 mb-2"><span className="font-medium">Objet :</span> {preview.objet}</p>
              <div className="rounded-lg border bg-white" data-testid="relance-preview-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html) }} />
              {preview.sms && (
                <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50/50 p-3" data-testid="relance-preview-sms">
                  <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 mb-1.5">Aperçu SMS</p>
                  <p className="text-sm whitespace-pre-line">{preview.sms}</p>
                  <p className="text-[11px] text-slate-400 mt-1.5">{preview.sms.length} caractères</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompanyTab({ company, setCompany }) {
  const set = (k, v) => setCompany((p) => ({ ...(p || {}), [k]: v }));
  const c = company || {};
  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-6">
        <p className="text-sm text-slate-500 mb-4">Ces informations composent l'en-tête et le pied de page des devis, factures et bons de livraison.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {IMG_FIELDS.map(([k, label]) => <ImgUpload key={k} label={label} value={c[k]} onChange={(v) => set(k, v)} />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMPANY_FIELDS.filter((f) => f.key !== "logo_url").map((f) => (
            <div key={f.key} className={f.col === 2 || f.type === "textarea" ? "md:col-span-2" : ""}>
              <Label className="text-xs">{f.label}</Label>
              {f.type === "textarea"
                ? <Textarea value={c[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`company-${f.key}`} className="mt-1" rows={2} />
                : <Input value={c[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`company-${f.key}`} className="mt-1" />}
            </div>
          ))}
        </div>
      </div>
      <EmailBannerBlock c={c} set={set} />
      <RelanceBlock c={c} set={set} />
    </div>
  );
}

function DepartmentsTab({ poles }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/departments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const schema = [
    { name: "nom", label: "Nom du département", required: true },
    { name: "code", label: "Code" },
    { name: "pole", label: "Pôle associé", type: "select", options: poles || [] },
    { name: "actif", label: "Actif ?", type: "select", options: [{ value: true, label: "Actif" }, { value: false, label: "Désactivé" }] },
    { name: "description", label: "Description", type: "textarea" },
  ];
  const save = async (form) => {
    try {
      const payload = { ...form, actif: form.actif === true || form.actif === "true" };
      if (editing) await api.put(`/departments/${editing.id}`, payload); else await api.post("/departments", payload);
      toast.success("Département enregistré"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce département ?")) return; await api.delete(`/departments/${id}`); toast.success("Supprimé"); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-department" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau département</Button>
      </div>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Building} title="Aucun département" subtitle="Créez les départements et associez-les à un pôle." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Nom</TableHead><TableHead>Code</TableHead><TableHead>Pôle</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`department-row-${d.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{d.nom}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{d.code || "—"}</TableCell>
                  <TableCell className="text-slate-600">{d.pole || "—"}</TableCell>
                  <TableCell><StatusBadge value={d.actif === false ? "Inactif" : "Actif"} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`department-menu-${d.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(d); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(d.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le département" : "Nouveau département"} schema={schema} initial={editing || { actif: true }} onSubmit={save} />
    </div>
  );
}

function VehiclesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/vehicles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const schema = [
    { name: "immatriculation", label: "Immatriculation", required: true }, { name: "type", label: "Type" },
    { name: "marque", label: "Marque" }, { name: "modele", label: "Modèle" },
    { name: "responsable", label: "Responsable / conducteur habituel" },
    { name: "statut", label: "Statut", type: "select", options: VEHICLE_STATUSES },
    { name: "notes", label: "Informations complémentaires", type: "textarea" },
  ];
  const save = async (form) => {
    try { if (editing) await api.put(`/vehicles/${editing.id}`, form); else await api.post("/vehicles", form); toast.success("Véhicule enregistré"); load(); }
    catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce véhicule ?")) return; await api.delete(`/vehicles/${id}`); toast.success("Supprimé"); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-vehicle" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau véhicule</Button></div>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Truck} title="Aucun véhicule" subtitle="Ajoutez les véhicules utilisés pour les livraisons." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Immatriculation</TableHead><TableHead>Type</TableHead><TableHead>Marque / Modèle</TableHead><TableHead>Responsable</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((v) => (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold font-mono">{v.immatriculation}</TableCell>
                  <TableCell className="text-slate-600">{v.type || "—"}</TableCell>
                  <TableCell className="text-slate-600">{[v.marque, v.modele].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell className="text-slate-600">{v.responsable || "—"}</TableCell>
                  <TableCell><StatusBadge value={v.statut} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`vehicle-menu-${v.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(v); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(v.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le véhicule" : "Nouveau véhicule"} schema={schema} initial={editing || { statut: "Actif" }} onSubmit={save} />
    </div>
  );
}

function ProfilesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [perms, setPerms] = useState({});
  const load = () => api.get("/perm-profiles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setLabel(""); setPerms({}); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setLabel(p.label); setPerms({ ...(p.perms || {}) }); setOpen(true); };
  const save = async () => {
    if (!label.trim()) { toast.error("Nom du profil requis"); return; }
    try {
      if (editing) await api.put(`/perm-profiles/${editing.id}`, { label, perms });
      else await api.post("/perm-profiles", { label, perms });
      toast.success("Profil enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce profil ?")) return; await api.delete(`/perm-profiles/${id}`); toast.success("Supprimé"); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Créez vos propres profils de droits, appliqués en 1 clic à la création d'un utilisateur (en plus des 5 profils fournis).</p>
        <Button onClick={openNew} data-testid="add-profile" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau profil</Button>
      </div>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Aucun profil personnalisé" subtitle="Les 5 profils fournis (Chef de chantier, Livreur, Commercial, Graphiste, Assistante administrative) restent disponibles." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Profil</TableHead><TableHead>Modules configurés</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`profile-row-${p.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{p.label}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{Object.keys(p.perms || {}).length} module(s)</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`profile-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(p)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier le profil" : "Nouveau profil de droits"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nom du profil</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : Conducteur de travaux" className="mt-1" data-testid="profile-label" /></div>
            <p className="text-[11px] text-slate-400">Cochez les actions autorisées par module. Non coché = non accordé par ce profil.</p>
            <PermMatrix value={perms} onChange={setPerms} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} data-testid="profile-save" className="bg-brand-600 text-white">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REPORT_TPL_FIELDS = [
  ["activites", "Activités réalisées"], ["taches", "Tâches effectuées"], ["resultats", "Résultats obtenus"],
  ["difficultes", "Difficultés rencontrées"], ["points_suivi", "Points à suivre"],
  ["taches_demain", "Tâches prévues demain"], ["commentaires", "Commentaires"],
];
function ReportTemplatesTab({ functions }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tpl, setTpl] = useState(null);
  const load = () => api.get("/settings").then((r) => setItems(r.data.report_templates || []));
  useEffect(() => { load(); }, []);
  const persist = async (next) => { await api.put("/settings", { report_templates: next }); load(); };
  const openNew = () => { setEditing(null); setTpl({ label: "", fonction: "", activites: "", taches: "", resultats: "", difficultes: "", points_suivi: "", taches_demain: "", commentaires: "" }); setOpen(true); };
  const openEdit = (t, i) => { setEditing(i); setTpl({ ...t }); setOpen(true); };
  const setF = (k, v) => setTpl((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!tpl.label.trim()) { toast.error("Nom du modèle requis"); return; }
    const next = [...(items || [])];
    if (editing !== null) next[editing] = tpl; else next.push(tpl);
    try { await persist(next); toast.success("Modèle enregistré"); setOpen(false); } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (i) => { if (!window.confirm("Supprimer ce modèle ?")) return; const next = (items || []).filter((_, j) => j !== i); await persist(next); toast.success("Supprimé"); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Modèles de rapport journalier pré-remplis selon le métier — proposés au collaborateur lors de la création d'un rapport.</p>
        <Button onClick={openNew} data-testid="add-report-template" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau modèle</Button>
      </div>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="Aucun modèle" subtitle="Créez des modèles pré-remplis par métier." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Modèle</TableHead><TableHead>Métier / fonction</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t, i) => (
                <TableRow key={i} data-testid={`report-template-row-${i}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{t.label}</TableCell>
                  <TableCell className="text-slate-600">{t.fonction || "—"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`report-template-menu-${i}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(t, i)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(i)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing !== null ? "Modifier le modèle" : "Nouveau modèle de rapport"}</DialogTitle></DialogHeader>
          {tpl && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Nom du modèle</Label><Input value={tpl.label} onChange={(e) => setF("label", e.target.value)} placeholder="Ex : Chef de chantier" className="mt-1" data-testid="report-template-label" /></div>
                <div><Label className="text-xs">Métier / fonction</Label>
                  <Select value={tpl.fonction || "none"} onValueChange={(v) => setF("fonction", v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="report-template-fonction"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— Aucune —</SelectItem>{(functions || []).map((fn) => <SelectItem key={fn} value={fn}>{fn}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {REPORT_TPL_FIELDS.map(([k, lab]) => (
                <div key={k}><Label className="text-xs">{lab}</Label><Textarea value={tpl[k] || ""} onChange={(e) => setF(k, e.target.value)} rows={2} className="mt-1" data-testid={`report-template-${k}`} /></div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} data-testid="report-template-save" className="bg-brand-600 text-white">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader />;
  const setCompany = (updater) => setS((p) => ({ ...p, company: typeof updater === "function" ? updater(p.company) : updater }));
  const save = async () => {
    try {
      const payload = {}; ALL_KEYS.forEach((k) => (payload[k] = s[k] || []));
      payload.company = s.company || {};
      payload.article_subcategory_map = s.article_subcategory_map || {};
      await api.put("/settings", payload); toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <PageHeader title="Paramètres" subtitle="Listes configurables par rubrique, départements, documents et véhicules">
        <Button onClick={save} data-testid="save-settings" className="bg-brand-600 hover:bg-brand-700 text-white"><Save className="h-4 w-4 mr-1.5" />Enregistrer</Button>
      </PageHeader>
      <Tabs defaultValue="listes">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="listes" data-testid="tab-listes">Listes</TabsTrigger>
          <TabsTrigger value="departements" data-testid="tab-departements">Départements</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">En-tête & pied de page</TabsTrigger>
          <TabsTrigger value="profils" data-testid="tab-profils">Profils de droits</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">E-mails envoyés</TabsTrigger>
          <TabsTrigger value="modeles" data-testid="tab-modeles">Modèles de rapports</TabsTrigger>
          <TabsTrigger value="vehicules" data-testid="tab-vehicules">Véhicules</TabsTrigger>
        </TabsList>
        <TabsContent value="listes"><ListsTab s={s} setS={setS} /></TabsContent>
        <TabsContent value="departements"><DepartmentsTab poles={s.poles} /></TabsContent>
        <TabsContent value="documents"><CompanyTab company={s.company} setCompany={setCompany} /></TabsContent>
        <TabsContent value="profils"><ProfilesTab /></TabsContent>
        <TabsContent value="emails"><EmailLogsTab /></TabsContent>
        <TabsContent value="modeles"><ReportTemplatesTab functions={s.functions} /></TabsContent>
        <TabsContent value="vehicules"><VehiclesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
